import { api } from './client'

// ============================================================================
// Lô phân loại — pattern PNK + PXK với items M-N (Phase 3-lots-redesign)
// Status sống trên phiếu: pending|approved|rejected|cancelled
// ============================================================================

export type NoteStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'

export type ScentingLotInReason =
    | 'from_process'
    | 'adjustment'
    | 'return_from_packaging_process'
    | 'from_stocktake_surplus'
export type ScentingLotOutReason = 'for_packaging' | 'discard' | 'adjustment'

export interface ScentingLot {
    id: string
    code: string
    grade_id: string
    grade_name?: string
    quantity_in: number
    quantity_available: number
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
}

export interface ScentingLotStockInNoteItem {
    id: string
    note_id: string
    scenting_lot_id: string
    scenting_lot_code?: string
    grade_id?: string | null
    grade_name?: string
    quantity: number
    created_at: string
    /** Nguồn tầng dòng (reason=from_process): danh sách lô phân loại nguồn + kg. */
    sources?: ScentingLotStockInNoteItemSource[]
}

export interface ScentingLotStockInNoteItemSource {
    id: string
    note_item_id: string
    source_sorting_lot_id: string
    source_quantity: number
    source_sorting_lot_code?: string
}

export interface ScentingLotStockInNote {
    id: string
    code: string
    status: NoteStatus
    reason: ScentingLotInReason
    /** PXK lô phân loại nguồn — có khi reason = from_process. */
    sorting_lot_stock_out_note_id?: string | null
    sorting_lot_stock_out_note_code?: string
    /** Phiếu lấy hàng kho phân loại đã chọn làm gợi ý (chỉ ghi lại, cho sửa phiếu). */
    sorting_lot_withdrawal_note_id?: string | null
    stocktake_session_id?: string | null
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    approved_by_id?: string | null
    approved_by_name?: string
    approved_at?: string | null
    rejected_reason?: string | null
    cancelled_at?: string | null
    created_at: string
    updated_at: string
    items_count?: number
    items?: ScentingLotStockInNoteItem[]
}

export type ScentingLotStockOutNoteItem = ScentingLotStockInNoteItem

export interface ScentingLotStockOutNote extends Omit<
    ScentingLotStockInNote,
    'reason' | 'items'
> {
    reason: ScentingLotOutReason
    items?: ScentingLotStockOutNoteItem[]
    /** PXK đã bị một PNK sản phẩm sống link (chỉ có ở API detail). */
    has_active_in_note?: boolean
}

// ============================================================================
// Phiếu lấy hàng (withdrawal note) — phiếu độc lập, không đụng tồn kho.
// Vòng đời: open|closed|cancelled. Không có bước duyệt.
// ============================================================================

export type WithdrawalNoteStatus = 'open' | 'closed' | 'cancelled'

export interface ScentingLotWithdrawalNoteItem {
    id: string
    note_id: string
    scenting_lot_id: string
    scenting_lot_code?: string
    grade_id?: string | null
    grade_name?: string
    quantity: number
    created_at: string
}

export interface ScentingLotWithdrawalNote {
    id: string
    code: string
    status: WithdrawalNoteStatus
    notes?: string | null
    created_by_id?: string | null
    created_by_name?: string
    created_at: string
    updated_at: string
    items_count?: number
    items?: ScentingLotWithdrawalNoteItem[]
}

export interface CreateWithdrawalNoteItemInput {
    scenting_lot_id: string
    quantity: number
}

export interface CreateWithdrawalNoteRequest {
    notes?: string
    items: CreateWithdrawalNoteItemInput[]
}

export interface ListWithdrawalNotesParams {
    status?: WithdrawalNoteStatus
    search?: string
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export interface ScentingLotHistoryEntry {
    note_id: string
    note_code: string
    note_type: 'in' | 'out'
    status: NoteStatus
    reason: string
    quantity: number
    notes?: string | null
    created_at: string
    approved_at?: string | null
    created_by_name?: string
}

// ============================================================================
// Request shapes
// ============================================================================

/** Một lô nguồn của một dòng phiếu nhập. */
export interface ItemSourceInput {
    source_lot_id: string
    source_quantity: number
}

/** Nguồn tầng dòng — danh sách nhiều lô, chỉ gửi khi reason=from_process. */
interface ItemSource {
    sources?: ItemSourceInput[]
}

export type CreateInNoteItemInput =
    | ({
          scenting_lot_id: string
          quantity: number
      } & ItemSource)
    | ({
          new_lot_grade_id: string
          new_lot_code?: string
          quantity: number
      } & ItemSource)

export interface CreateInNoteRequest {
    reason: ScentingLotInReason
    /**
     * BẮT BUỘC khi reason = from_process. Trỏ tới PXK lô phân loại
     * (approved + reason=for_scenting). Không có validate khối lượng.
     */
    sorting_lot_stock_out_note_id?: string
    /**
     * Tuỳ chọn — phiếu lấy hàng kho phân loại dùng làm gợi ý lô nguồn. KHÔNG lưu
     * trên phiếu; nếu có, BE trừ số dư còn lại của note khi tạo (đóng khi cạn).
     */
    source_withdrawal_note_id?: string
    notes?: string
    items: CreateInNoteItemInput[]
}

/** Sửa PNK lô lấy hương đang pending. Reason bất biến. */
export interface UpdateInNoteRequest {
    reason: ScentingLotInReason
    sorting_lot_stock_out_note_id?: string
    /** Ghi lại phiếu lấy hàng đã chọn (không re-consume khi sửa). */
    source_withdrawal_note_id?: string
    notes?: string
    items: CreateInNoteItemInput[]
}

export interface CreateOutNoteItemInput {
    scenting_lot_id: string
    quantity: number
}

export interface CreateOutNoteRequest {
    reason: ScentingLotOutReason
    notes?: string
    items: CreateOutNoteItemInput[]
}

/** Sửa PXK lô lấy hương đang pending. Reason bất biến. */
export interface UpdateOutNoteRequest {
    reason: ScentingLotOutReason
    notes?: string
    items: CreateOutNoteItemInput[]
}

export interface ListLotsParams {
    grade_id?: string
    search?: string
    only_available?: '1' | undefined
    page?: number
    per_page?: number
}

export interface ListNotesParams {
    status?: NoteStatus
    reason?: string
    date_from?: string
    date_to?: string
    /** Lọc PNK theo PXK lô phân loại nguồn. Chỉ có tác dụng với listInNotes. */
    sorting_lot_stock_out_note_id?: string
    page?: number
    per_page?: number
}

// ============================================================================
// API endpoints
// ============================================================================

const BASE = '/scenting-lots'

export const scentingLotApi = {
    // Lots
    listLots: (params?: ListLotsParams) =>
        api.get<ScentingLot[]>(BASE, { params }),
    getLot: (id: string) => api.get<ScentingLot>(`${BASE}/${id}`),
    getLotHistory: (
        id: string,
        params?: { page?: number; per_page?: number },
    ) =>
        api.get<ScentingLotHistoryEntry[]>(`${BASE}/${id}/history`, { params }),

    // Stock-in notes
    listInNotes: (params?: ListNotesParams) =>
        api.get<ScentingLotStockInNote[]>(`${BASE}/stock-in-notes`, { params }),
    getInNote: (id: string) =>
        api.get<ScentingLotStockInNote>(`${BASE}/stock-in-notes/${id}`),
    createInNote: (req: CreateInNoteRequest) =>
        api.post<ScentingLotStockInNote>(`${BASE}/stock-in-notes`, req),
    updateInNote: (id: string, req: UpdateInNoteRequest) =>
        api.put<ScentingLotStockInNote>(`${BASE}/stock-in-notes/${id}`, req),
    approveInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/approve`),
    rejectInNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/reject`, { reason }),
    cancelInNote: (id: string) =>
        api.post<void>(`${BASE}/stock-in-notes/${id}/cancel`),

    // Stock-out notes
    listOutNotes: (params?: ListNotesParams) =>
        api.get<ScentingLotStockOutNote[]>(`${BASE}/stock-out-notes`, {
            params,
        }),
    /**
     * PXK lô lấy hương đã duyệt, lý do "xuất đi đóng gói" — nguồn cho picker khi
     * tạo PNK sản phẩm. Không lọc PXK đã dùng (1 PXK sinh bao nhiêu PNK cũng
     * được) nên BE giới hạn 100 dòng; dùng search để thu hẹp.
     */
    listOutNotesForPackaging: (search?: string) =>
        api.get<ScentingLotStockOutNote[]>(
            `${BASE}/stock-out-notes/for-packaging`,
            { params: search ? { search } : undefined },
        ),
    getOutNote: (id: string) =>
        api.get<ScentingLotStockOutNote>(`${BASE}/stock-out-notes/${id}`),
    createOutNote: (req: CreateOutNoteRequest) =>
        api.post<ScentingLotStockOutNote>(`${BASE}/stock-out-notes`, req),
    updateOutNote: (id: string, req: UpdateOutNoteRequest) =>
        api.put<ScentingLotStockOutNote>(`${BASE}/stock-out-notes/${id}`, req),
    approveOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/approve`),
    rejectOutNote: (id: string, reason: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/reject`, { reason }),
    cancelOutNote: (id: string) =>
        api.post<void>(`${BASE}/stock-out-notes/${id}/cancel`),

    // Phiếu lấy hàng (withdrawal notes)
    listWithdrawalNotes: (params?: ListWithdrawalNotesParams) =>
        api.get<ScentingLotWithdrawalNote[]>(`${BASE}/withdrawal-notes`, {
            params,
        }),
    getWithdrawalNote: (id: string) =>
        api.get<ScentingLotWithdrawalNote>(`${BASE}/withdrawal-notes/${id}`),
    createWithdrawalNote: (req: CreateWithdrawalNoteRequest) =>
        api.post<ScentingLotWithdrawalNote>(`${BASE}/withdrawal-notes`, req),
    cancelWithdrawalNote: (id: string) =>
        api.post<void>(`${BASE}/withdrawal-notes/${id}/cancel`),
}
